import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import moment from 'moment';
import { cloneDeep } from 'lodash-es';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { AREAS_MAPPING } from '@/pages/intelligence/components/TeaSelector/AreaSelect/constants';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import { getAllCountries } from '@/pages/intelligence/utils/regionSelectUtils';
import { getInitialInputList, getInitialFormValue } from './utils';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 组合国家筛选器
  COMPOSED_COUNTRY_SELECT as COMPOSED_COUNTRY_SELECT_FILTER,
  // 单选区域筛选器
  SINGLE_AREA_SELECT as SINGLE_AREA_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { FILTER_DEFAULT_INPUT_LIST, DEFAULT_FORM_VALUE } from './config';
import './index.scss';

import type {
  IBaseProps,
  IRegion,
  IDateTypely,
  IDateType,
} from '@/pages/intelligence/types/common';
import type {
  IStoreRankTopFilter,
  IStoreRankTopFetchParams,
} from '@/pages/intelligence/types/storeRank';

interface IConsoleStoreRankTopFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  isOperate: boolean;
  isMonthlyRank?: boolean;
  getIsMonthlyRank?: (payload: Partial<IStoreRankTopFilter>) => boolean;
  isRegionVisible?: boolean;
  isRegionDisabled?: boolean;
  regionOptions?: IRegion[] | null;
  defaultRegions?: string[];
  typeList: IDateType[];
  granularity?: IDateTypely;
  startDate?: string;
  endDate?: string;
  multiCountryText?: React.ReactNode;
  onClickMultiCountry?: () => void;
  onConfirm?: (payload: Partial<IStoreRankTopFetchParams>) => void;
  onReset?: () => void;
}

const ConsoleStoreRankTopFilter: React.FC<IConsoleStoreRankTopFilterProps> = (props) => {
  const {
    style,
    className,
    iconText,
    isOperate,
    isMonthlyRank = false,
    getIsMonthlyRank,
    isRegionVisible = true,
    isRegionDisabled = false,
    regionOptions,
    defaultRegions,
    typeList,
    granularity,
    startDate,
    endDate,
    multiCountryText,
    onClickMultiCountry,
    onConfirm,
    onReset,
  } = props;

  const { formatMessage } = useIntl();

  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { filterExtend, setFilterExtend, resetFilterExtend } = useFilterExtend({});

  // 筛选器的初始值
  const initialFormValue = getInitialFormValue(isOperate, filterExtend, DEFAULT_FORM_VALUE);

  // 筛选器UI渲染需要的value
  const formValue = useRef<any>(initialFormValue);

  // 筛选器初始配置
  const initialInputList = getInitialInputList(FILTER_DEFAULT_INPUT_LIST, {
    typeList,
    composedCountryVisible: isRegionVisible,
    composedCountryDisabled: isRegionDisabled,
    composedCountryDisabledLabel: formatMessage({ id: 'dbi_global' }),
  });

  // 筛选器控件配置
  const [inputList, setInputList] = useState(initialInputList);

  // 更新下拉国家筛选器控件配置
  const updateComposedCountryCfg = (
    inputList: any[],
    cfg: {
      isMonthlyRank: boolean;
      visible: boolean;
      disabled: boolean;
      options?: IRegion[] | null;
      multiCountryText?: React.ReactNode;
      onClickMultiCountry?: () => void;
    },
  ) => {
    if (!Array.isArray(inputList)) return;
    const { isMonthlyRank, visible, disabled, options, multiCountryText: multiText, onClickMultiCountry: onMultiCountry } = cfg || {};
    const composedCountryFilterCfg = inputList.find(
      (item) => item?.key === COMPOSED_COUNTRY_SELECT_FILTER,
    );
    if (!composedCountryFilterCfg) return;
    composedCountryFilterCfg.visible = visible && !isMonthlyRank ? 1 : 2;
    composedCountryFilterCfg.disabled = !!disabled;
    composedCountryFilterCfg.options = Array.isArray(options) ? options : [];
    composedCountryFilterCfg.disabledLabel = disabled ? formatMessage({ id: 'dbi_global' }) : '';
    composedCountryFilterCfg.multiCountryText = !disabled ? multiText : undefined;
    composedCountryFilterCfg.onClickMultiCountry = !disabled ? onMultiCountry : undefined;
    composedCountryFilterCfg.overlayClassName = 'dbi-alltopcharts-region-cascader-select-overlay';
  };

  // 更新单选区域筛选器控件配置
  const updateSingleAreaCfg = (inputList: any[], cfg: { isMonthlyRank: boolean }) => {
    if (!Array.isArray(inputList)) return;
    const { isMonthlyRank } = cfg || {};
    const singleAreaFilterCfg = inputList.find((item) => item?.type === SINGLE_AREA_SELECT_FILTER);
    if (!singleAreaFilterCfg) return;
    singleAreaFilterCfg.visible = isMonthlyRank ? 1 : 2;
  };

  useEffect(() => {
    if (!isMonthlyRank) return;
    const singleAreaFormValue = formValue.current[SINGLE_AREA_SELECT_FILTER]?.value;
    if (!singleAreaFormValue) {
      formValue.current = {
        ...formValue.current,
        [SINGLE_AREA_SELECT_FILTER]: {
          value: AREAS_MAPPING.NORTH_AMERICA,
        },
      };
    }
  }, [isMonthlyRank]);

  /**
   * 更新各个筛选器的options数据
   */
  useEffect(() => {
    const inputListTmp = cloneDeep(inputList);
    // 更新下拉国家筛选器控件配置
    updateComposedCountryCfg(inputListTmp, {
      isMonthlyRank,
      visible: isRegionVisible,
      disabled: isRegionDisabled,
      options: regionOptions,
      multiCountryText,
      onClickMultiCountry,
    });
    // 更新单选区域筛选器控件配置
    updateSingleAreaCfg(inputListTmp, { isMonthlyRank });
    setInputList(inputListTmp);
  }, [
    isMonthlyRank,
    isRegionVisible,
    isRegionDisabled,
    regionOptions,
    multiCountryText,
    onClickMultiCountry,
    formatMessage,
  ]);

  /**
   * 校验初始化 继承时 继承的勾选的国家的有效性，即当前页面的国家列表数据 包含 勾选的国家 才继承
   */
  useEffect(() => {
    // Nintendo 没有国家筛选器，禁用的，不处理
    if (!isRegionVisible || isRegionDisabled) return;
    // PlayStation月粒度榜单，不是国家筛选器，是单选区域筛选器，也不处理
    if (isMonthlyRank) return;
    // 没有获取到国家列表数据，也不处理
    if (!regionOptions?.length) return;
    const allCountries = getAllCountries(regionOptions);
    const composedCountryFormValue = formValue.current?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountries = composedCountryFormValue?.countries;
    const isComposedCountriesValid = allCountries?.includes(composedCountries?.[0]);
    if (isComposedCountriesValid) return;
    formValue.current = {
      ...formValue.current,
      [COMPOSED_COUNTRY_SELECT_FILTER]: {
        value: {
          ...formValue.current[COMPOSED_COUNTRY_SELECT_FILTER].value,
          countries:
            Array.isArray(defaultRegions) && defaultRegions.length > 0 ? defaultRegions : ['us'],
        },
      },
    };
  }, [isRegionVisible, isRegionDisabled, isMonthlyRank, regionOptions]);

  /**
   * 同步父组件传入的时间
   */
  useEffect(() => {
    const rangeTimeValue = formValue.current?.[RANGE_TIME_SELECT_FILTER];
    formValue.current = {
      ...formValue.current,
      [RANGE_TIME_SELECT_FILTER]: {
        type: granularity
          ? feachTimeTypeToFilterTimeType(granularity)
          : rangeTimeValue?.type ?? 'date',
        value:
          startDate && endDate ? [moment(startDate), moment(endDate)] : rangeTimeValue?.value ?? [],
      },
    };
  }, [granularity, startDate, endDate]);

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    formValue.current = value;
    const apiValueTmp = { ...apiValue };
    const rangeTime = apiValueTmp?.[RANGE_TIME_SELECT_FILTER];
    const granularityTmp = rangeTime?.granularity ?? 'daily';
    const isMonthlyRankTmp = getIsMonthlyRank?.({ granularity: granularityTmp }) || false;
    if (isMonthlyRankTmp && !apiValueTmp?.[SINGLE_AREA_SELECT_FILTER]?.value) {
      apiValueTmp[SINGLE_AREA_SELECT_FILTER].value = AREAS_MAPPING.NORTH_AMERICA;
    }
    const composedCountry = apiValueTmp?.[COMPOSED_COUNTRY_SELECT_FILTER]?.value;
    const composedCountries = composedCountry?.countries;
    const countries = composedCountries?.length === 1 ? composedCountries : ['us'];
    const singleArea = apiValueTmp?.[SINGLE_AREA_SELECT_FILTER]?.value;
    const areas = [singleArea];
    const regions = isMonthlyRankTmp ? areas : countries;
    const startDateTmp = rangeTime?.start_date || '';
    const endDateTmp = rangeTime?.end_date || '';

    const filterTmp = {
      regions,
      granularity: granularityTmp,
      start_date: startDateTmp,
      end_date: granularityTmp === 'monthly' ? startDateTmp : endDateTmp,
    };
    onConfirm?.(filterTmp);
    setFilterExtend(apiValueTmp);
  };

  const handleReset = () => {
    formValue.current = {
      ...DEFAULT_FORM_VALUE,
      [RANGE_TIME_SELECT_FILTER]: {
        type: 'date',
        value: [moment(), moment()],
      },
    };
    resetFilterExtend();
    onReset?.();
  };

  return (
    <Filter
      style={style}
      className={classnames(
        'dbi-store-rank-more-filter dbi-console-store-rank-more-filter',
        className,
      )}
      iconText={iconText}
      showButton={false}
      inputList={inputList}
      defaultValue={DEFAULT_FORM_VALUE}
      value={formValue.current}
      onConfirm={handleConfirm}
      onReset={handleReset}
    />
  );
};

export default ConsoleStoreRankTopFilter;
